import type { Appointment, AppointmentDossier } from './types';
import { DOSSIER_ENRICHMENTS } from './appointmentDossierEnrichments';
import {
  REFERENCE_WALKIN_HEADLINES,
  REFERENCE_WALKIN_SUMMARY,
  REFERENCE_WALKOUT_SUMMARY,
  referenceWalkoutHeadlines,
} from './referenceOpportunity';

export const CAMPBELL_DOSSIER: AppointmentDossier = {
  demographics: 'Female · 70 · Patient since May 2013',
  operatory: 'Op-3 (quiet)',
  kioskCode: '1359',
  clinicalAlerts: {
    allergies: ['Penicillin', 'Latex', 'Sulfa', 'Aspirin', 'Codeine'],
    conditions: [
      'Mild hypertension',
      'Sleep apnea (CPAP)',
      'Type 2 diabetes',
      'Osteoporosis',
      'GERD',
      'Anxiety disorder',
    ],
    medications: ['Metformin 1000mg', 'Lisinopril 20mg', 'Atorvastatin 40mg', 'Sertraline 50mg'],
    mindful: ['Sensitive to needles', 'Anxiety: not in control', 'Slow to respond to anesthesia'],
  },
  opportunityWindow: {
    walkin: {
      summary: REFERENCE_WALKIN_SUMMARY,
      headlines: REFERENCE_WALKIN_HEADLINES,
    },
    walkout: {
      summary: REFERENCE_WALKOUT_SUMMARY,
      headlines: referenceWalkoutHeadlines('$45 — copay for today\'s services'),
    },
  },
  insurance: {
    planLabel: 'Sunlife PPO',
    tellPatient:
      'Your cleaning and exam today are fully covered. For the deep cleaning Dr. Carter mentioned, we will submit for approval today and call you when approved — usually 2–3 days.',
    collectToday: '$45 — copay for today\'s services',
    handled: [
      'Eligibility verified yesterday at 4:12pm',
      'Today\'s preventive visit covered 100%, frequency 2× / yr',
      'X-rays (D0274) eligible — last set 14 months ago',
    ],
    todo: [
      'Pre-auth needed for SRP (D4341) — submit today',
      'Annual max balance: $1,400 / $2,000 used',
      'Schedule deep cleaning after approval (typically 2–3 days)',
    ],
  },
  doctorsNote:
    'Had a bad dental experience as a child — never pushes back but shuts down if she feels rushed. Always offer nitrous before any procedure. Loves to talk about her grandkids.',
  lastInteraction:
    'Cleaning, Apr 23. Was 15 min late. Hygienist flagged early perio pocketing on lower anteriors — Dr. Carter to evaluate today. Mentioned interest in whitening.',
  family: [
    { name: 'Jane Brooks', rel: 'Spouse', status: 'Also a patient' },
    { name: 'Emily', rel: 'Daughter', status: '8mo overdue', overdue: true },
    { name: 'Michael', rel: 'Son', status: 'Also a patient' },
  ],
  comfort: [
    { label: 'Coffee', value: 'Black' },
    { label: 'Music', value: 'Jazz' },
    { label: 'Blanket', value: 'Yes' },
    { label: 'Sedation', value: 'Nitrous (anxiety)' },
  ],
  patientValue: [
    { label: 'Total value', value: '$11,240' },
    { label: 'Health score', value: '07 / 10' },
    { label: 'Relationship', value: '08 / 10' },
    { label: 'Balance', value: '$340', tone: 'warning' },
  ],
  notes: [
    {
      author: 'Mary (Front desk)',
      when: 'Yesterday, 4:32 PM',
      text: 'Called to confirm — needs parking validation. Reserved spot 12.',
    },
    {
      author: 'Dr. Wilkinson',
      when: 'May 18, 11:08 AM',
      text: 'Husband Jane called — interested in consult. Pass to Sara when Mrs C is here. Do not push.',
    },
  ],
};

type DossierPatch = {
  demographics: string;
  operatory: string;
  kioskCode: string;
  doctorsNote: string;
  lastInteraction: string;
  insurance: AppointmentDossier['insurance'];
  clinicalAlerts?: Partial<AppointmentDossier['clinicalAlerts']>;
  opportunityWalkinSummary?: string[];
  family?: AppointmentDossier['family'];
  comfort?: AppointmentDossier['comfort'];
  patientValue?: AppointmentDossier['patientValue'];
  notes?: AppointmentDossier['notes'];
};

const APPOINTMENT_PATCHES: Record<string, DossierPatch> = {
  a1: {
    demographics: 'Male · 58 · Patient since 2019',
    operatory: 'Op-1 (general)',
    kioskCode: '2841',
    doctorsNote: 'Prefers morning appointments. Crown seat went smoothly last visit.',
    lastInteraction: 'Crown prep, Mar 12. All ELVA checks passed before today\'s seat appointment.',
    insurance: {
      planLabel: 'Delta Dental PPO',
      tellPatient: 'Today\'s crown seat is covered at 50% after deductible. Your estimated patient portion is ready at checkout.',
      collectToday: '$582 — estimated patient portion',
      handled: ['Eligibility verified this morning', 'Crown (D2740) on file — frequency OK', 'Benefits refreshed at 8:02am'],
      todo: ['Confirm shade with patient pre-op'],
    },
  },
  a2: {
    demographics: 'Female · 34 · Patient since 2021',
    operatory: 'Op-2 (general)',
    kioskCode: '3102',
    doctorsNote: 'Birthday today — team usually sings if she is comfortable with it.',
    lastInteraction: 'Composite, Jan 8. Mentioned wedding planning — interested in whitening consult.',
    insurance: {
      planLabel: 'MetLife DHMO',
      tellPatient: 'Your composite today is covered. Happy birthday — today\'s preventive portion is fully covered too.',
      collectToday: '$0 — no copay due today',
      handled: ['Eligibility active', 'Restorative frequency OK', 'Preventive 100% covered'],
      todo: ['Offer whitening info sheet at checkout'],
    },
    family: [{ name: 'James Harrell', rel: 'Spouse', status: 'Also a patient' }],
  },
  a3: {
    demographics: 'Male · 45 · Patient since 2017',
    operatory: 'Op-4 (hygiene)',
    kioskCode: '1198',
    doctorsNote: 'Forms still incomplete — send tablet link at check-in.',
    lastInteraction: 'Prophy, Oct 2. Balance flagged on account — gentle reminder at checkout.',
    insurance: {
      planLabel: 'Cigna DPPO',
      tellPatient: 'Your exam and cleaning are covered. We also have an outstanding balance from last visit — we can review payment options today.',
      collectToday: '$127 — prior balance + $25 copay',
      handled: ['Preventive visit covered 100%', 'Forms link sent 9:14am — not completed'],
      todo: ['Complete intake forms before provider handoff', 'Discuss payment plan for $102 balance'],
    },
  },
  a4: {
    demographics: 'Male · 29 · New patient',
    operatory: 'Op-3 (quiet)',
    kioskCode: '4022',
    doctorsNote: 'New patient with anxiety flag — allow extra time, offer nitrous proactively.',
    lastInteraction: 'First visit — no prior chart history in PMS.',
    insurance: {
      planLabel: 'Aetna PPO',
      tellPatient: 'Welcome! Your new-patient exam is covered at 100%. We verified your benefits this morning — any X-rays ordered today are eligible.',
      collectToday: '$0 — new patient promo applied',
      handled: ['Eligibility verified 7:55am', 'D0150 covered — new patient slot confirmed', 'Benefits packet emailed'],
      todo: ['Complete full medical history on tablet', 'Schedule hygiene if perio noted'],
    },
    opportunityWalkinSummary: [
      'First visit — set a warm, unhurried tone. Anxiety flag means Op-3 and nitrous offer before exam.',
      'Capture household insurance info — patient mentioned fiancée may switch to same plan.',
    ],
  },
  a5: {
    demographics: 'Female · 52 · Patient since 2014',
    operatory: 'Op-1 (general)',
    kioskCode: '5560',
    doctorsNote: 'Unconfirmed — call script used twice. Often runs 10 min late.',
    lastInteraction: 'Filling, Dec 5. Did not confirm via text — front desk called yesterday.',
    insurance: {
      planLabel: 'Guardian PPO',
      tellPatient: 'We have not received confirmation for today — your filling is still scheduled. Coverage looks active; copay applies at checkout.',
      collectToday: '$35 — copay if visit proceeds',
      handled: ['Eligibility active as of today'],
      todo: ['Confirm appointment before seating', 'Re-send confirmation text if no show risk'],
    },
  },
  a6: {
    demographics: 'Female · 41 · Patient since 2020',
    operatory: 'Op-5 (hygiene)',
    kioskCode: '2877',
    doctorsNote: 'Reliable attendee — prefers Daniel for hygiene.',
    lastInteraction: 'Prophy, Nov 14. All clear — recall due today.',
    insurance: {
      planLabel: 'Sunlife PPO',
      tellPatient: 'Your cleaning today is fully covered — no copay for preventive.',
      collectToday: '$0 — preventive covered 100%',
      handled: ['Eligibility verified', 'D1110 frequency OK (2nd this year)', 'All forms complete'],
      todo: ['Send 6-month recall reminder at checkout'],
    },
  },
  a7: {
    demographics: 'Male · 63 · Patient since 2012',
    operatory: 'Op-2 (general)',
    kioskCode: '3310',
    doctorsNote: 'Crown + buildup case — verify temp comfort before dismissal.',
    lastInteraction: 'Buildup, Apr 30. Temp crown intact — seat scheduled next month.',
    insurance: {
      planLabel: 'Blue Cross Dental',
      tellPatient: 'Today\'s crown delivery is covered at 50%. We estimated your portion when we prepped — same amount due today.',
      collectToday: '$415 — crown seat portion',
      handled: ['Pre-auth approved Mar 28', 'Lab case received May 18', 'Patient confirmed via text'],
      todo: ['Verify occlusion post-seat', 'Schedule follow-up if sensitivity'],
    },
  },
  a8: {
    demographics: 'Male · 38 · Patient since 2018',
    operatory: 'Op-1 (general)',
    kioskCode: '4821',
    doctorsNote: 'No-show pattern — requires card on file for future bookings.',
    lastInteraction: 'No-show, today 3pm slot. Third unconfirmed miss in 12 months.',
    insurance: {
      planLabel: 'United Concordia',
      tellPatient: 'We missed you today — call us to reschedule. Your crown prep benefits remain active through year-end.',
      collectToday: '$0 — no-show, no charge today',
      handled: ['Automated no-show text sent 3:15pm'],
      todo: ['Call patient to reschedule', 'Flag chart for confirmation policy', 'Review broken-appt fee policy'],
    },
  },
  a9: {
    demographics: 'Male · 47 · Patient since 2016',
    operatory: 'Op-2 (general)',
    kioskCode: '3901',
    doctorsNote: 'Rescheduled endo consult — prefers afternoon slots only.',
    lastInteraction: 'Rescheduled from today to May 28. Forms incomplete for consult.',
    insurance: {
      planLabel: 'Principal PPO',
      tellPatient: 'Your consult was moved to May 28 — we will re-verify benefits before that visit.',
      collectToday: '$0 — rescheduled, no charge today',
      handled: ['Consult slot moved on calendar', 'Reschedule confirmation emailed'],
      todo: ['Complete forms before May 28', 'Verify endo referral on file'],
    },
  },
  a10: {
    demographics: 'Male · 55 · Patient since 2010',
    operatory: 'Op-4 (surgical)',
    kioskCode: '1893',
    doctorsNote: 'VIP referrer — sent 4 patients last quarter. Always thank him personally.',
    lastInteraction: 'Implant follow-up, Feb 20. Healing well — Dr. Carter pleased with integration.',
    insurance: {
      planLabel: 'Sunlife PPO',
      tellPatient: 'Today\'s implant follow-up is covered. No copay for this post-op check.',
      collectToday: '$0 — post-op included',
      handled: ['Implant post-op covered', 'Imaging from Feb on file', 'Referral thank-you note sent Q1'],
      todo: ['Update implant stability notes', 'Ask if anyone else needs scheduling'],
    },
    patientValue: [
      { label: 'Referrals YTD', value: '4 patients', tone: 'green' },
      { label: 'Total value', value: '$48,200' },
      { label: 'Health score', value: '09 / 10', tone: 'green' },
      { label: 'Relationship', value: '10 / 10', tone: 'green' },
    ],
  },
  a11: {
    demographics: CAMPBELL_DOSSIER.demographics,
    operatory: CAMPBELL_DOSSIER.operatory,
    kioskCode: CAMPBELL_DOSSIER.kioskCode,
    doctorsNote: CAMPBELL_DOSSIER.doctorsNote,
    lastInteraction: CAMPBELL_DOSSIER.lastInteraction,
    insurance: CAMPBELL_DOSSIER.insurance,
    opportunityWalkinSummary: CAMPBELL_DOSSIER.opportunityWindow.walkin.summary,
    family: CAMPBELL_DOSSIER.family,
    comfort: CAMPBELL_DOSSIER.comfort,
    patientValue: CAMPBELL_DOSSIER.patientValue,
    notes: CAMPBELL_DOSSIER.notes,
    clinicalAlerts: CAMPBELL_DOSSIER.clinicalAlerts,
  },
  a12: {
    demographics: 'Male · 67 · Patient since 2008',
    operatory: 'Op-4 (surgical)',
    kioskCode: '2201',
    doctorsNote: 'Coumadin on file — confirm INR before implant. Medical alert needs physician clearance.',
    lastInteraction: 'Consult, May 10. Pre-auth submitted — implant broken appointment today.',
    insurance: {
      planLabel: 'Aetna Medicare',
      tellPatient: 'Your implant case needs updated medical clearance before we reschedule. Pre-authorization is still pending with Aetna.',
      collectToday: '$0 — broken appointment, no charge today',
      handled: ['Pre-auth submitted May 11', 'Medical history flagged for INR'],
      todo: [
        'Confirm INR within 48h of reschedule',
        'Obtain physician clearance for implant',
        'Call patient to rebook after med clear',
      ],
    },
    clinicalAlerts: {
      allergies: ['Penicillin'],
      conditions: ['Atrial fibrillation', 'On Coumadin', 'Osteoporosis'],
      medications: ['Warfarin 5mg', 'Metoprolol 25mg'],
      mindful: ['Bleeding risk — confirm INR', 'Medical alert needs confirmation'],
    },
  },
  a13: {
    demographics: 'Female · 50 · Patient since 2015',
    operatory: 'Op-5 (hygiene)',
    kioskCode: '1444',
    doctorsNote: 'SRP in progress — quadrant 1 today. Good anesthesia response.',
    lastInteraction: 'SRP start, May 6. Quadrant 2 scheduled in 2 weeks.',
    insurance: {
      planLabel: 'Delta Dental PPO',
      tellPatient: 'Today\'s periodontal treatment is covered at 80%. We will collect your estimated portion at checkout.',
      collectToday: '$88 — SRP copay',
      handled: ['SRP (D4341) pre-auth approved', 'Quadrant 1 of 4 in progress', 'Post-op instructions given May 6'],
      todo: ['Schedule quadrant 2 within 14 days', 'Confirm chlorhexidine rinse pickup'],
    },
  },
  a14: {
    demographics: 'Male · 61 · Patient since 2022',
    operatory: 'Op-3 (quiet)',
    kioskCode: '3771',
    doctorsNote: 'Big case — full mouth rehab consult. TC coordinator looped in.',
    lastInteraction: 'Phone consult, May 15. Sent imaging link — CBCT uploaded yesterday.',
    insurance: {
      planLabel: 'MetLife PDP Plus',
      tellPatient: 'Today is a consult — no treatment fee. We are waiting on pre-authorization for the proposed treatment plan — typically 5–7 business days.',
      collectToday: '$0 — consult only',
      handled: ['CBCT received May 19', 'Consult fee waived — new patient', 'Treatment plan draft in TC queue'],
      todo: [
        'Present phased treatment plan today',
        'Submit pre-auth for phase 1 ($4,200)',
        'Schedule TC follow-up call within 48h',
      ],
    },
    opportunityWalkinSummary: [
      'Full mouth rehab consult — allow 90 minutes. TC coordinator should greet before provider.',
      'Pre-auth pending on SRP phase — mention timeline transparently if asked.',
    ],
  },
  a15: {
    demographics: 'Female · 70 · Patient since May 2013',
    operatory: 'Op-2 (general)',
    kioskCode: '1359',
    doctorsNote: 'Same patient as a11 — crown seat today. Nitrous if anxiety spikes.',
    lastInteraction: 'New patient exam, today 12pm — crown prep scheduled same day.',
    insurance: {
      planLabel: 'Sunlife PPO',
      tellPatient: 'Your crown seat today is covered at 50%. We already verified benefits at your earlier visit — same copay structure applies.',
      collectToday: '$285 — crown seat portion',
      handled: ['Eligibility verified today', 'D2740 on treatment plan', 'Shade selected at prep visit'],
      todo: ['Confirm occlusion and contacts', 'Schedule follow-up if sensitivity > 48h'],
    },
    family: CAMPBELL_DOSSIER.family,
    comfort: CAMPBELL_DOSSIER.comfort,
  },
  a16: {
    demographics: 'Female · 52 · Patient since 2014',
    operatory: 'Op-4 (surgical)',
    kioskCode: '5561',
    doctorsNote: 'Bridge seat — verify lab shade match before cement.',
    lastInteraction: 'Bridge try-in, May 12. Patient approved shade A2.',
    insurance: {
      planLabel: 'Guardian PPO',
      tellPatient: 'Your bridge seat is covered at 50%. Lab case arrived yesterday — we are ready to seat today.',
      collectToday: '$220 — bridge seat portion',
      handled: ['Lab case in-house May 19', 'Try-in approved May 12', 'Forms complete except financial consent'],
      todo: ['Obtain signed financial consent', 'Post-op care instructions at checkout'],
    },
  },
  a17: {
    demographics: 'Female · 48 · Patient since 2019',
    operatory: 'Op-2 (general)',
    kioskCode: '7742',
    doctorsNote: 'Lab case missing — do not seat. Patient aware of delay.',
    lastInteraction: 'Rescheduled from today — lab not arrived for crown D2740.',
    insurance: {
      planLabel: 'Cigna DPPO',
      tellPatient: 'We rescheduled because your crown has not arrived from the lab yet. There is no charge for today — we will call when it is in.',
      collectToday: '$0 — lab delay, no charge today',
      handled: ['Lab ticket #8842 — ETA May 22', 'Patient notified by text 4:45pm'],
      todo: ['Track lab case #8842', 'Call patient when case arrives', 'Rebook within 5 days of lab receipt'],
    },
  },
  a18: {
    demographics: 'Male · 72 · Patient since 2005',
    operatory: 'Op-5 (hygiene)',
    kioskCode: '9012',
    doctorsNote: 'Long-time patient — hard of hearing, speak clearly and face him when talking.',
    lastInteraction: 'Prophy, Nov 20. Completed early — prefers first slot of day.',
    insurance: {
      planLabel: 'Medicare + AARP supplement',
      tellPatient: 'Your cleaning today is fully covered under your supplemental plan.',
      collectToday: '$0 — preventive covered',
      handled: ['Eligibility verified', 'D1110 frequency OK', 'Medical history updated Jan'],
      todo: ['Update medical history if any new medications since January'],
    },
  },
  a19: {
    demographics: 'Male · 56 · Patient since 2013',
    operatory: 'Op-5 (hygiene)',
    kioskCode: '4420',
    doctorsNote: 'Unconfirmed perio maintenance — balance on account $89.',
    lastInteraction: 'Perio maintenance, Aug 12. Balance flagged — unconfirmed today.',
    insurance: {
      planLabel: 'Humana PPO',
      tellPatient: 'Your perio visit is covered at 80%. There is also a small balance from August — we can review at checkout.',
      collectToday: '$89 prior balance + $40 copay est.',
      handled: ['Eligibility active', 'Perio frequency OK'],
      todo: ['Confirm appointment', 'Collect or plan for $89 balance'],
    },
  },
  a20: {
    demographics: 'Male · 44 · Patient since 2018',
    operatory: 'Op-5 (hygiene)',
    kioskCode: '1188',
    doctorsNote: 'In treatment — prophy started 20 min ago.',
    lastInteraction: 'Prophy started today 1pm. No issues reported.',
    insurance: {
      planLabel: 'Sunlife PPO',
      tellPatient: 'Your cleaning is in progress and fully covered — no copay expected.',
      collectToday: '$0 — preventive',
      handled: ['Seated 1:04pm', 'Forms complete', 'Provider: Daniel Scott'],
      todo: ['Complete post-op instructions at checkout'],
    },
  },
  a21: {
    demographics: 'Male · 68 · Patient since 2011',
    operatory: 'Op-5 (hygiene)',
    kioskCode: '3309',
    doctorsNote: 'Birthday today — prefers handshake over singing.',
    lastInteraction: 'Prophy, Dec 1. Stable perio — recall every 6 months.',
    insurance: {
      planLabel: 'Delta Dental PPO',
      tellPatient: 'Happy birthday! Your cleaning today is fully covered.',
      collectToday: '$0 — preventive covered',
      handled: ['Eligibility verified', 'All ELVA checks passed'],
      todo: ['Offer birthday card at checkout'],
    },
  },
  // Orville Allison (p001) — the Invisalign mock patient (amendment 34 §1/§4).
  a22: {
    demographics: 'Male · 34 · Patient since 2018',
    operatory: 'Op 2',
    kioskCode: '5011',
    doctorsNote: 'Clear aligner candidate — the Invisalign card carries what to say.',
    lastInteraction: 'Filling, Aug 3. Asked the Invis-Line about cost on 24 Sep.',
    insurance: {
      planLabel: 'Delta Dental PPO · active',
      tellPatient:
        'Your Delta Dental PPO is active today. Today\'s filling is covered; your share is small.',
      collectToday: '$40 — patient share, today',
      handled: ['Eligibility checked today 8:42 am', 'Delta Dental PPO · active'],
      todo: ['Orthodontic benefit unused — see the Aligners card'],
    },
  },
};

function mergeClinical(
  base: AppointmentDossier['clinicalAlerts'],
  patch?: Partial<AppointmentDossier['clinicalAlerts']>,
): AppointmentDossier['clinicalAlerts'] {
  if (!patch) return base;
  return {
    allergies: patch.allergies ?? base.allergies,
    conditions: patch.conditions ?? base.conditions,
    medications: patch.medications ?? base.medications,
    mindful: patch.mindful ?? base.mindful,
  };
}

function buildFromPatch(patch: DossierPatch, id: string, _appt: Appointment): AppointmentDossier {
  const enrich = DOSSIER_ENRICHMENTS[id];
  if (!enrich) return CAMPBELL_DOSSIER;

  const clinicalAlerts = patch.clinicalAlerts
    ? mergeClinical(enrich.clinicalAlerts, patch.clinicalAlerts)
    : enrich.clinicalAlerts;

  const insurance: AppointmentDossier['insurance'] = {
    ...patch.insurance,
    handled:
      patch.insurance.handled.length > 0
        ? patch.insurance.handled
        : ['Eligibility verified for today\'s visit'],
    todo:
      patch.insurance.todo.length > 0
        ? patch.insurance.todo
        : ['Confirm next recall interval at checkout'],
  };

  return {
    demographics: patch.demographics,
    operatory: patch.operatory,
    kioskCode: patch.kioskCode,
    clinicalAlerts,
    opportunityWindow: {
      walkin: {
        summary: patch.opportunityWalkinSummary ?? enrich.opportunityWalkinSummary,
        headlines: REFERENCE_WALKIN_HEADLINES,
      },
      walkout: {
        summary: enrich.opportunityWalkoutSummary,
        headlines: referenceWalkoutHeadlines(patch.insurance.collectToday),
      },
    },
    insurance,
    doctorsNote: patch.doctorsNote,
    lastInteraction: patch.lastInteraction,
    family: patch.family ?? enrich.family,
    comfort: patch.comfort ?? enrich.comfort,
    patientValue: patch.patientValue ?? enrich.patientValue,
    notes: patch.notes && patch.notes.length > 0 ? patch.notes : enrich.notes,
  };
}

/** Per-appointment dossier for day view detail modal. */
export function getAppointmentDossier(appt: Appointment): AppointmentDossier {
  const patch = APPOINTMENT_PATCHES[appt.id];
  if (patch) return buildFromPatch(patch, appt.id, appt);
  return CAMPBELL_DOSSIER;
}
